import { defineStore } from 'pinia';
import { CurrentUser } from '@/dashboard/types/current_user';
import { useAppStore } from '@/dashboard/store/appStore';
import { showNotification } from '@/dashboard/vue/notifications/notification_actions';
import { AccountApi, UpdateCurrentUserProps } from '@/dashboard/api/account';
import { sendGoogleEvent } from '@/dashboard/helper';
import { PaymentApi } from '@/dashboard/api/payment';
import { Workspace } from '@/dashboard/types/workspace';
import { WithManagementSubscription } from '@/dashboard/types/subscription';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import paddle_constants from '../constants/paddle';
import { PaymentStep } from './purchaseStore';

type SettingsStore = {
  paddle?: Paddle;
  isShowDeletePopup: boolean;
  isDeleteAccountLoader: boolean;
  deleteAccountConfirmation: string;
  deleteAccountError: boolean;
  workspaces: (Omit<Workspace, 'subscription'> & { subscription: WithManagementSubscription })[];
  isShowSaveChangesBanner: boolean;
  isShowSaveChangesLoader: boolean;
  isSavedChanges: boolean;
  isShowAvatarLoader: boolean;
  usernameModel: string;
  emailModel: string;
  currentPasswordModel: string;
  passwordModel: string;
  passwordConfirmModel: string;
  emailError: string | null;
  currentPasswordError: string | null;
  passwordError: string | null;
};

export const useSettingsStore = defineStore('settingsStore', {
  state: (): SettingsStore => ({
    isShowDeletePopup: false,
    isDeleteAccountLoader: false,
    deleteAccountConfirmation: '',
    deleteAccountError: false,
    workspaces: [],
    isShowSaveChangesBanner: false,
    isShowSaveChangesLoader: false,
    isSavedChanges: false,
    isShowAvatarLoader: false,
    usernameModel: '',
    emailModel: '',
    currentPasswordModel: '',
    passwordModel: '',
    passwordConfirmModel: '',
    emailError: null,
    currentPasswordError: null,
    passwordError: null,
  }),
  getters: {},
  actions: {
    async initPaddle() {
      this.paddle = await initializePaddle({
        environment: paddle_constants.environment,
        token: paddle_constants.token,
        checkout: {
          settings: {
            allowLogout: false,
            showAddTaxId: false,
            theme: 'light',
          },
        },
        eventCallback: async (event) => {},
      });
    },
    discardChanges() {
      this.resetSettingsModel();
      this.isShowSaveChangesBanner = false;
    },
    async saveChanges() {
      const appStore = useAppStore();
      let user = appStore.currentUser;
      const params = {
        name: this.usernameModel,
        email: this.emailModel,
        currentPassword: this.currentPasswordModel,
        password: this.passwordModel,
        passwordConfirmation: this.passwordConfirmModel,
      } satisfies UpdateCurrentUserProps;

      try {
        this.isShowSaveChangesLoader = true;
        const data = await AccountApi.updateCurrentUserSettings(params);
        this.isShowSaveChangesLoader = false;

        this.resetSettingsModelErrors();

        if (data.errors) {
          this.emailError = data.errors?.email?.[0];
          this.currentPasswordError = data.errors?.current_password?.[0];
          this.passwordError = data.errors?.password?.[0];
        } else if (data.user) {
          this.isSavedChanges = true;

          let title = '';

          if (data.letter_send && user && params.email !== data.user.email && this.passwordModel) {
            title = 'Confirm the changes. Two confirmations have been sent to your email';
          } else if ((data.letter_send && user && params.email !== data.user.email) || (data.letter_send && user && this.passwordModel)) {
            title = 'Confirm the changes. Confirmation has been sent to your email';
          }

          if (data.letter_send) {
            showNotification('any_type', {
              title,
              emoji: '❕',
              ok_action: () => {},
            });
          }

          await appStore.loadCurrentUser();
          user = appStore.currentUser;

          if (user) {
            this.setSettingsUserModel(user).then();
          }

          setTimeout(() => {
            this.isSavedChanges = false;
            this.setIsOpenSaveBanner(false);
          }, 1300);
        }
      } finally {
        this.isShowSaveChangesLoader = false;
      }
    },
    async changeAvatar(file: File) {
      const appStore = useAppStore();
      this.isShowAvatarLoader = true;
      try {
        const data = await AccountApi.updateCurrentUserAvatar(file);
        appStore.setCurrentUser(data.user);
      } finally {
        this.isShowAvatarLoader = false;
      }
    },
    async removeAvatar() {
      const appStore = useAppStore();
      this.isShowAvatarLoader = true;
      try {
        const data = await AccountApi.removeCurrentUserAvatar();
        appStore.setCurrentUser(data.user);
      } finally {
        this.isShowAvatarLoader = false;
      }
    },
    async setSettingsUserModel(user: CurrentUser) {
      this.emailModel = user.email;
      this.usernameModel = user.name;
      this.currentPasswordModel = '';
      this.passwordModel = '';
      this.passwordConfirmModel = '';

      this.resetSettingsModelErrors();

      PaymentApi.getWorkspacesSubscription().then((response) => {
        this.workspaces = response.workspaces;
      });
    },
    resetSettingsModel() {
      const appStore = useAppStore();
      const user = appStore.currentUser;
      if (user) {
        this.setSettingsUserModel(user).then();
        this.resetSettingsModelErrors();
      }
    },
    resetSettingsModelErrors() {
      this.emailError = null;
      this.currentPasswordError = null;
      this.passwordError = null;
    },
    setIsOpenSaveBanner(isOpen: boolean) {
      this.isShowSaveChangesBanner = isOpen;
    },
    toggleDeleteAccount() {
      this.isShowDeletePopup = !this.isShowDeletePopup;
      this.isDeleteAccountLoader = false;
      this.deleteAccountConfirmation = '';
      this.deleteAccountError = false;
    },
    async deleteAccount() {
      this.deleteAccountError = false;
      if (this.deleteAccountConfirmation !== 'Delete') {
        this.deleteAccountError = true;
        return;
      }
      try {
        this.isDeleteAccountLoader = true;
        await AccountApi.deleteAccount();
        this.isDeleteAccountLoader = false;
        sendGoogleEvent('user_deleted');
        window.location.reload();
      } catch {
        this.isDeleteAccountLoader = false;
      }
    },
  },
});
